import type { ColumnDef } from '@tanstack/react-table';

import { Can } from '@/components/can/Can';
import { DataTableColumnHeader } from '@/components/dataTable/ColumnHeader';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { EmailOpenIcon } from '@/icons/email-open-icon';
import { UndoRoundIcon } from '@/icons/undo-round-icon';
import { formatDate } from '@/lib/dateFormatter';
import { Inquiry } from '@/services/faq/faq.types';
import { InquiryDetails } from '../InquiryDetails';

export type ModalSize = 'sm' | 'md' | 'lg';
type ModalVariant = 'default' | 'destructive' | 'warning';

type OpenConfirmationProps = (data: {
  title: string;
  description?: string;
  cancelLabel: string;
  actionLabel: string;
  content?: React.ReactNode;
  size?: ModalSize;
  variant?: ModalVariant;
  onAction: () => Promise<void>;
  onCancel?: () => Promise<void>;
}) => void;

export function getColumns(t: (key: string) => string, openConfirmation: OpenConfirmationProps): ColumnDef<Inquiry>[] {
  return [
    {
      accessorKey: 'sn',
      header: ({ column }) => (
        <DataTableColumnHeader className="whitespace-nowrap" column={column} title={t('common.page.tableColumn.sn')} />
      ),
      cell: ({ row, table }) => <div className="font-medium">{getSerialNumber(table, row.index)}</div>,
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'inquiryId',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('faq.inquiry.table.inquiryId')} />,
      cell: ({ row }) => <div className="text-caption font-medium text-primary">{row.getValue('inquiryId')}</div>,
      enableHiding: false,
    },
    {
      accessorKey: 'nickName',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('faq.inquiry.table.nickname')} />,
      cell: ({ row }) => <div className="text-caption font-medium text-primary">{row.getValue('nickName')}</div>,
      enableHiding: false,
    },
    {
      accessorKey: 'email',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('faq.inquiry.table.email')} />,
      cell: ({ row }) => <div className="text-caption font-medium text-link underline">{row.getValue('email')}</div>,
      enableHiding: false,
    },
    {
      accessorKey: 'phoneNumber',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('faq.inquiry.table.phoneNumber')} />,
      cell: ({ row }) => <div className="text-caption font-medium text-primary">{row.getValue('phoneNumber')}</div>,
      enableHiding: false,
    },
    {
      accessorKey: 'inquiryType',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('faq.inquiry.table.inquiryType')} />,
      cell: ({ row }) => (
        <div className="text-caption font-medium text-primary max-w-[213px] truncate">
          {row.getValue('inquiryType') ? row.getValue('inquiryType') : '-'}
        </div>
      ),
      enableHiding: false,
    },
    {
      accessorKey: 'message',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('faq.inquiry.table.message')} />,
      cell: ({ row }) => (
        <div className="text-caption font-medium text-primary break-all max-w-[285px] truncate">
          {row.getValue('message')}
        </div>
      ),
      enableHiding: false,
    },
    {
      id: 'createdDate',
      accessorKey: 'createdDate',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('faq.inquiry.table.postedDate')} />,
      cell: ({ row }) => {
        const date = new Date(row.getValue<Date>('createdDate'));
        return <div className="text-caption font-medium text-primary">{date ? formatDate(date) : '-'}</div>;
      },
      meta: { label: 'Created Date' },
      enableHiding: false,
      enableColumnFilter: true,
    },
    {
      accessorKey: 'actions',
      header: ({ column }) => (
        <DataTableColumnHeader className="whitespace-nowrap" column={column} title={t('faq.inquiry.table.actions')} />
      ),
      cell: ({ row }) => (
        <div className="flex gap-4 items-center">
          <Can rule={{ allOf: [PERMS.inquiry.details] }}>
            <EmailOpenIcon
              className="text-muted-foreground size-4 hover:cursor-pointer"
              onClick={() => {
                const inquiryId = row.original.inquiryId;
                openConfirmation({
                  title: t('faq.inquiry.inquiryDetailsModel.title'),
                  actionLabel: t('faq.inquiry.inquiryDetailsModel.button.reply'),
                  size: 'lg',
                  cancelLabel: t('faq.inquiry.inquiryDetailsModel.button.cancel'),
                  onCancel: async () => {},
                  onAction: async () => {
                    window.location.href = `mailto:${row.original.email}`;
                    await Promise.resolve();
                  },
                  content: <InquiryDetails inquiryId={inquiryId} />,
                });
              }}
            />
          </Can>
          <UndoRoundIcon
            className="text-foreground-icon size-4 hover:cursor-pointer"
            onClick={() => {
              window.location.href = `mailto:${row.original.email}`;
            }}
          />
        </div>
      ),
      enableHiding: false,
      enableSorting: false,
    },
  ];
}
